import { Control, Controller, FieldValues, Path } from 'react-hook-form';

import { Select } from '@/components/Form/Select';

import { useAddressCityList } from '@/hooks/useAddressCityList';
import { useTranslate } from '@/hooks/useTranslate';

import { getCorrectCountryCode } from '@/utils/countryTranslations';

import { AddressCityTextField } from './StandardAddressFormFields';

type AddressCityFieldProps<TFieldValues extends FieldValues> = Readonly<{
  name: Path<TFieldValues>;
  control: Control<TFieldValues>;
  isPending: boolean;
  userTerritoryCode?: string;
  selectedCountry: string | undefined;
  textFieldPending?: boolean;
  hideLabel?: boolean;
}>;

export function AddressCityField<TFieldValues extends FieldValues>({
  name,
  control,
  isPending,
  userTerritoryCode = '',
  selectedCountry,
  textFieldPending,
  hideLabel = false,
}: AddressCityFieldProps<TFieldValues>) {
  const { t } = useTranslate();
  const { isCityListAvailable, cityOptions } = useAddressCityList();
  const shouldUseCityList = isCityListAvailable(selectedCountry);
  const isCountrySameTenant =
    getCorrectCountryCode(userTerritoryCode) ===
    getCorrectCountryCode(selectedCountry);

  if (shouldUseCityList && cityOptions) {
    return (
      <Controller
        name={name}
        control={control}
        rules={{
          required: isCountrySameTenant
            ? t('createNewAccount.addressPage.form.city.error.required')
            : undefined,
        }}
        render={({ field, fieldState }) => (
          <Select
            {...field}
            control={control}
            options={cityOptions}
            error={!!fieldState.error}
            helperText={fieldState.error?.message}
            fullWidth
            disabled={!isCountrySameTenant || isPending}
            label={
              hideLabel ? '' : t('createNewAccount.addressPage.form.city.label')
            }
            inputProps={{
              'aria-label': t('createNewAccount.addressPage.form.city.label'),
            }}
            useDrawerOnMobile
          />
        )}
      />
    );
  }

  return (
    <AddressCityTextField
      name={name}
      control={control}
      isPending={textFieldPending ?? isPending}
      hideLabel={hideLabel}
    />
  );
}
